import { useState, useEffect, useCallback } from 'react'
import {
  FileText, RefreshCw, Shield, Cloud, Globe, Newspaper, Download, Printer,
  TrendingUp, AlertTriangle, Bug, Lightbulb, Brain, CheckCircle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

export default function BriefingPage() {
  const [threatLevel, setThreatLevel] = useState<any>(null)
  const [cloudProviders, setCloudProviders] = useState<any[]>([])
  const [signals, setSignals] = useState<any[]>([])
  const [trends, setTrends] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [threatRes, cloudRes, signalsRes, trendsRes] = await Promise.allSettled([
        api.get('/api/cyber/threat-level'),
        api.get('/api/cloud/status'),
        api.get('/api/signals/feed', { params: { limit: 50 } }),
        api.get('/api/trends/live'),
      ])
      if (threatRes.status === 'fulfilled') setThreatLevel(threatRes.value.data)
      if (cloudRes.status === 'fulfilled') setCloudProviders(cloudRes.value.data.providers || [])
      if (signalsRes.status === 'fulfilled') setSignals(signalsRes.value.data.signals || [])
      if (trendsRes.status === 'fulfilled') setTrends(trendsRes.value.data)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const criticalSignals = signals.filter(s => s.severity === 'critical' || s.severity === 'high')
  const cloudIssues = cloudProviders.filter(p => p.overall_status !== 'operational')
  const topCompanies = (trends?.trending_companies || []).slice(0, 5)
  const topTech = (trends?.trending_technologies || []).slice(0, 5)
  const topTopics = (trends?.trending_topics || []).slice(0, 5)

  const now = new Date()
  const dateStr = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="space-y-6 animate-fade-in max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <FileText size={24} className="text-blue" /> Executive Briefing
          </h1>
          <p className="text-sm text-text-secondary mt-1">{dateStr}</p>
        </div>
        <div className="flex gap-2">
          <a href="/api/export/briefing" target="_blank" rel="noopener" className="btn-secondary text-xs">
            <Printer size={12} /> Print/PDF
          </a>
          <a href="/api/export/briefing-text" target="_blank" rel="noopener" className="btn-secondary text-xs">
            <Download size={12} /> Text
          </a>
          <button type="button" onClick={fetchAll} className="btn-secondary text-xs">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading && !threatLevel && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Compiling briefing...</span>
        </div>
      )}

      {/* ── Threat Summary ──────────────────────────────────────────────── */}
      {threatLevel && (
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-4">
            <Shield size={16} className="text-rose" /> Cyber Threat Summary
          </h2>
          <div className="grid grid-cols-4 gap-4 mb-4">
            <div className="text-center">
              <p className={clsx('text-2xl font-bold', threatLevel.level === 'critical' ? 'text-rose' : threatLevel.level === 'elevated' ? 'text-amber' : 'text-emerald')}>
                {threatLevel.level?.toUpperCase()}
              </p>
              <p className="text-[10px] text-text-muted">Threat Level</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-text-primary">{threatLevel.critical_cves_7d}</p>
              <p className="text-[10px] text-text-muted">Critical CVEs (7d)</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-text-primary">{threatLevel.kev_additions_7d}</p>
              <p className="text-[10px] text-text-muted">Exploited in Wild</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-text-primary">{threatLevel.ransomware_linked}</p>
              <p className="text-[10px] text-text-muted">Ransomware-Linked</p>
            </div>
          </div>
          <p className="text-xs text-text-secondary">{threatLevel.summary}</p>
        </div>
      )}

      {/* ── Cloud Status Summary ────────────────────────────────────────── */}
      <div className="card p-6">
        <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-4">
          <Cloud size={16} className="text-cyan" /> Cloud & Infrastructure
        </h2>
        {cloudIssues.length === 0 ? (
          <div className="flex items-center gap-2 text-emerald text-sm">
            <CheckCircle size={16} /> All {cloudProviders.length} providers operational
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-amber-light"><AlertTriangle size={12} className="inline mr-1" />{cloudIssues.length} provider(s) with issues:</p>
            {cloudIssues.map(p => (
              <div key={p.slug} className="flex items-center gap-2 text-xs py-1">
                <span className={clsx('w-2 h-2 rounded-full', p.overall_status === 'outage' ? 'bg-rose' : 'bg-amber')} />
                <span className="font-medium text-text-primary">{p.name}</span>
                <span className="text-text-muted">{p.overall_status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Critical Signals ────────────────────────────────────────────── */}
      {criticalSignals.length > 0 && (
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-4">
            <AlertTriangle size={16} className="text-amber" /> Critical & High Priority Signals ({criticalSignals.length})
          </h2>
          <div className="space-y-2">
            {criticalSignals.slice(0, 10).map((s, i) => (
              <div key={i} className="flex items-start gap-2 py-1.5 border-b border-border last:border-0">
                <span className={clsx('badge text-[10px] shrink-0', s.severity === 'critical' ? 'badge-red' : 'badge-amber')}>
                  {s.severity}
                </span>
                <div className="flex-1">
                  <p className="text-xs text-text-primary">{s.title}</p>
                  <p className="text-[10px] text-text-muted">{s.source}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Trending Now ────────────────────────────────────────────────── */}
      {trends && (
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-emerald" /> Trending Now (from {trends.total_articles_scanned} articles)
          </h2>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <h3 className="text-[10px] text-text-muted uppercase tracking-wider font-semibold mb-2">Companies</h3>
              {topCompanies.map((c: any, i: number) => (
                <div key={c.name} className="flex items-center justify-between py-1 text-xs">
                  <span className="text-text-primary">{i + 1}. {c.name}</span>
                  <span className="font-mono text-text-muted">{c.mentions}</span>
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-[10px] text-text-muted uppercase tracking-wider font-semibold mb-2">Technologies</h3>
              {topTech.map((t: any, i: number) => (
                <div key={t.name} className="flex items-center justify-between py-1 text-xs">
                  <span className="text-text-primary">{i + 1}. {t.name}</span>
                  <span className="font-mono text-text-muted">{t.mentions}</span>
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-[10px] text-text-muted uppercase tracking-wider font-semibold mb-2">Topics</h3>
              {topTopics.map((t: any, i: number) => (
                <div key={t.name} className="flex items-center justify-between py-1 text-xs">
                  <span className="text-text-primary">{i + 1}. {t.name}</span>
                  <span className="font-mono text-text-muted">{t.mentions}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Data Sources ────────────────────────────────────────────────── */}
      <div className="card p-4">
        <p className="text-[10px] text-text-muted text-center">
          Compiled from 120+ RSS feeds, NVD API, CISA KEV, {cloudProviders.length} cloud status feeds,
          USGS, ReliefWeb, Reuters, BBC, GitHub Search API, ArXiv.
          Data refreshed every 5-15 minutes via Celery workers.
        </p>
      </div>
    </div>
  )
}
